// client/src/pages/AdminWebSource.js - Web Source Analytics for Admin Panel
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const SOURCE_COLORS = {
  'Paid': '#EF4444', 'Organic Search': '#10B981', 'AI Search': '#8B5CF6',
  'Social': '#F59E0B', 'Email': '#3B82F6', 'Referral': '#06B6D4',
  'Direct': '#6B7280', 'Unknown': '#D1D5DB'
};

const PLATFORM_COLORS = [
  '#EF4444', '#F97316', '#F59E0B', '#10B981', '#06B6D4',
  '#3B82F6', '#8B5CF6', '#EC4899', '#6B7280', '#84CC16'
];

const s = {
  container: { maxWidth: 1400, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 24, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--ink)' },
  subtitle: { color: 'var(--muted)', fontSize: 13, marginTop: 4 },
  dateRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  dateInput: { padding: '8px 12px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none', color: 'var(--ink)', background: 'var(--card)' },
  liveChip: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 0, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid #A7F3D0', background: '#ECFDF5', color: '#059669' },
  liveChipOff: { border: '1px solid var(--line)', background: 'var(--paper-2)', color: 'var(--muted)' },
  liveDot: { width: 8, height: 8, borderRadius: '50%', background: '#10B981', animation: 'pulse 2s infinite' },
  refreshBtn: { background: 'none', border: '1px solid var(--line)', borderRadius: 0, padding: '7px 10px', cursor: 'pointer', fontSize: 14, color: 'var(--ink)' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 24 },
  statCard: { background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '18px 18px 14px', transition: 'all 0.2s' },
  statCardHighlight: { background: 'var(--ink)', color: 'var(--paper)', border: '1px solid var(--ink)' },
  statLabel: { fontSize: 11, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 6, color: 'var(--muted-2)' },
  statValue: { fontSize: 28, fontWeight: 700, fontFamily: 'var(--font-mono)', lineHeight: 1 },
  statSub: { fontSize: 11, marginTop: 6 },
  card: { background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: 24, marginBottom: 20 },
  cardTitle: { fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--muted-2)', marginBottom: 16, fontFamily: 'var(--font-mono)' },
  row: { display: 'grid', gap: 20, marginBottom: 20 },
  row2: { gridTemplateColumns: '1fr 2fr' },
  row3: { gridTemplateColumns: '1fr 1fr' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--muted-2)', borderBottom: '2px solid var(--line-card)', fontFamily: 'var(--font-mono)', background: 'var(--paper)' },
  thRight: { textAlign: 'right' },
  td: { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid var(--line-card)', color: 'var(--ink)' },
  tdRight: { textAlign: 'right', fontFamily: 'var(--font-mono)' },
  badge: { display: 'inline-flex', padding: '2px 8px', borderRadius: 0, fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-mono)' },
  progressBar: { height: 6, borderRadius: 0, background: 'var(--paper-2)', overflow: 'hidden', flex: 1 },
  progressFill: { height: '100%', borderRadius: 0, transition: 'width 0.3s' },
  convCell: { display: 'flex', alignItems: 'center', gap: 8 },
  exportBtn: { background: 'none', border: 'none', color: 'var(--muted)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-mono)' },
  roiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginTop: 12 },
  roiCard: { border: '1px solid var(--line-card)', borderRadius: 0, padding: 16, transition: 'all 0.2s' },
  roiCardGreen: { borderColor: '#D1FAE5', background: '#F0FDF4' },
  roiCardRed: { borderColor: '#FEE2E2', background: '#FEF2F2' },
  roiBadge: { display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 0, fontSize: 11, fontWeight: 700 },
  roiBadgeGreen: { background: '#D1FAE5', color: '#059669' },
  roiBadgeRed: { background: '#FEE2E2', color: '#DC2626' },
  roiBadgeFree: { background: '#D1FAE5', color: '#059669' },
  editBtn: { background: 'transparent', border: '1.5px solid var(--ink)', padding: '6px 12px', borderRadius: 0, fontSize: 12, cursor: 'pointer', color: 'var(--ink)' },
  saveBtn: { background: 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '6px 12px', borderRadius: 0, fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  cancelBtn: { background: 'transparent', border: '1.5px solid var(--ink)', padding: '6px 12px', borderRadius: 0, fontSize: 12, cursor: 'pointer', color: 'var(--ink)' },
  spendInput: { padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 13, width: '100%', outline: 'none', fontFamily: 'var(--font-mono)', color: 'var(--ink)', background: 'var(--card)' },
  spendBox: { background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 0, padding: 16, marginBottom: 12 }
};

const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) return;
  const headers = Object.keys(data[0]);
  const csvRows = [headers.join(','), ...data.map(row => headers.map(h => {
    const val = row[h] ?? '';
    return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
  }).join(','))];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  a.click(); URL.revokeObjectURL(url);
};

export default function AdminWebSource() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [countdown, setCountdown] = useState(15);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 3);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Ad Spend (localStorage persisted)
  const [adSpend, setAdSpend] = useState(() => {
    try { const saved = localStorage.getItem('th_ad_spend'); return saved ? JSON.parse(saved) : {}; } catch { return {}; }
  });
  const [editingSpend, setEditingSpend] = useState(false);
  const [tempSpend, setTempSpend] = useState({});

  const fetchData = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const { data: result } = await axios.get(`/api/admin/analytics/web-source?${params}`);
      if (result.success) setData(result.data);
      else throw new Error(result.error);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchData(); }, [startDate, endDate]); // eslint-disable-line

  useEffect(() => {
    if (autoRefresh) {
      setCountdown(15);
      countdownRef.current = setInterval(() => setCountdown(p => p <= 1 ? 15 : p - 1), 1000);
      intervalRef.current = setInterval(() => fetchData(true), 15000);
    }
    return () => { clearInterval(intervalRef.current); clearInterval(countdownRef.current); };
  }, [autoRefresh, startDate, endDate]); // eslint-disable-line

  const getSpend = (platform) => adSpend[platform] || 0;
  const startEditingSpend = () => { setTempSpend({ ...adSpend }); setEditingSpend(true); };
  const saveSpend = () => { setAdSpend(tempSpend); localStorage.setItem('th_ad_spend', JSON.stringify(tempSpend)); setEditingSpend(false); };

  // Derived data
  const totalVisitors = data?.summary?.totalVisitors || 0;
  const byType = data?.summary?.byType || [];
  const getTypeVal = (type, field) => { const f = byType.find(t => t.type === type); return f ? f[field] : 0; };

  const pieData = byType.map(t => ({ name: t.type, value: t.visitors, color: SOURCE_COLORS[t.type] || '#6B7280' }));

  const platformData = (data?.byPlatform || []).filter(p => p.platform !== 'Unknown').slice(0, 15);

  // Daily trend
  const trendMap = {};
  (data?.trend || []).forEach(t => {
    if (!trendMap[t._id.date]) trendMap[t._id.date] = { date: t._id.date };
    trendMap[t._id.date][t._id.type || 'Unknown'] = t.count;
  });
  const trendData = Object.values(trendMap).sort((a, b) => a.date.localeCompare(b.date));

  // Monthly
  const monthlyMap = {};
  (data?.monthlyTrend || []).forEach(t => {
    if (!monthlyMap[t._id.month]) monthlyMap[t._id.month] = { month: t._id.month, visitors: 0 };
    monthlyMap[t._id.month].visitors += t.count;
  });
  (data?.monthlyBookings || []).forEach(t => {
    if (!monthlyMap[t._id.month]) monthlyMap[t._id.month] = { month: t._id.month, visitors: 0 };
    monthlyMap[t._id.month].bookings = (monthlyMap[t._id.month].bookings || 0) + t.count;
    monthlyMap[t._id.month].revenue = (monthlyMap[t._id.month].revenue || 0) + t.revenue;
  });
  const monthlyData = Object.values(monthlyMap).sort((a, b) => a.month.localeCompare(b.month));

  const getConvColor = (rate) => parseFloat(rate) >= 3 ? '#10B981' : parseFloat(rate) >= 1 ? '#F59E0B' : '#EF4444';

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', color: 'var(--muted)' }}>Loading web source data...</div>;

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Web Source</h1>
          <p style={s.subtitle}>All website visitors by traffic source — with conversion rates, ROI & ad spend tracking</p>
        </div>
        <div style={s.dateRow}>
          <label style={{ fontSize: 11, color: 'var(--muted)' }}>From</label>
          <input type="date" style={s.dateInput} value={startDate} onChange={e => setStartDate(e.target.value)} />
          <label style={{ fontSize: 11, color: 'var(--muted)' }}>To</label>
          <input type="date" style={s.dateInput} value={endDate} onChange={e => setEndDate(e.target.value)} />
          <div
            style={{ ...s.liveChip, ...(autoRefresh ? {} : s.liveChipOff) }}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh && <span style={s.liveDot} />}
            {autoRefresh ? `Live ${countdown}s` : 'Auto-refresh off'}
          </div>
          <button style={s.refreshBtn} onClick={() => fetchData(true)} disabled={refreshing}>
            {refreshing ? '...' : '↻'}
          </button>
        </div>
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: 'var(--danger)', padding: '10px 14px', borderRadius: 0, marginBottom: 16, fontSize: 13 }}>{error}</div>}

      {/* Summary Stat Cards */}
      <div style={s.statsGrid}>
        <div style={{ ...s.statCard, ...s.statCardHighlight }}>
          <div style={{ ...s.statLabel, color: 'rgba(255,255,255,0.6)' }}>Total Visitors</div>
          <div style={s.statValue}>{totalVisitors.toLocaleString()}</div>
        </div>
        <div style={{ ...s.statCard, ...s.statCardHighlight, background: '#065F46', borderColor: '#065F46' }}>
          <div style={{ ...s.statLabel, color: 'rgba(255,255,255,0.6)' }}>Total Conversions</div>
          <div style={s.statValue}>{byType.reduce((sum, t) => sum + (t.bookings || 0), 0)}</div>
          <div style={{ ...s.statSub, color: 'rgba(255,255,255,0.5)' }}>
            ${(data?.byPlatform || []).reduce((sum, p) => sum + (p.revenue || 0), 0).toFixed(0)} rev
          </div>
        </div>
        {['Paid', 'Organic Search', 'AI Search', 'Direct'].map(type => (
          <div key={type} style={s.statCard}>
            <div style={{ ...s.statLabel, color: SOURCE_COLORS[type] }}>{type}</div>
            <div style={s.statValue}>{getTypeVal(type, 'visitors').toLocaleString()}</div>
            <div style={{ ...s.statSub, color: 'var(--muted)' }}>{getTypeVal(type, 'conversionRate') || '0.0'}% convert</div>
          </div>
        ))}
      </div>

      {/* ROI Comparison */}
      <div style={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <div style={s.cardTitle}>ROI Comparison</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: -8 }}>
              {editingSpend ? 'Enter your ad spend per platform, then save' : 'Click "Edit Ad Spend" to enter costs and see CPA, ROAS & profit'}
            </div>
          </div>
          {editingSpend ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <button style={s.saveBtn} onClick={saveSpend}>Save</button>
              <button style={s.cancelBtn} onClick={() => setEditingSpend(false)}>Cancel</button>
            </div>
          ) : (
            <button style={s.editBtn} onClick={startEditingSpend}>Edit Ad Spend</button>
          )}
        </div>

        {editingSpend && (
          <div style={s.spendBox}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Enter Ad Spend ({startDate} to {endDate})</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
              {['Google Ads', 'Meta (Facebook/Instagram)', 'TikTok Ads', 'LinkedIn Ads', 'Reddit Ads', 'Microsoft Ads', 'Snapchat Ads', 'Pinterest Ads', 'YouTube Ads'].map(name => (
                <div key={name}>
                  <label style={{ fontSize: 11, color: 'var(--muted)' }}>{name}</label>
                  <input
                    type="number" style={s.spendInput} placeholder="$0"
                    value={tempSpend[name] || ''}
                    onChange={e => setTempSpend(prev => ({ ...prev, [name]: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={s.roiGrid}>
          {platformData.map((p, i) => {
            const spend = getSpend(p.platform);
            const revenue = p.revenue || 0;
            const profit = revenue - spend;
            const roas = spend > 0 ? (revenue / spend) : 0;
            const isFree = spend === 0 && p.bookings > 0;
            const isGood = profit > 0 || isFree;
            return (
              <div key={i} style={{ ...s.roiCard, ...(isGood ? s.roiCardGreen : profit < 0 ? s.roiCardRed : {}) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{p.platform}</div>
                    <span style={{ ...s.badge, background: `${SOURCE_COLORS[p.type] || '#6B7280'}15`, color: SOURCE_COLORS[p.type], fontSize: 10, marginTop: 4 }}>{p.type}</span>
                  </div>
                  {isFree ? (
                    <span style={{ ...s.roiBadge, ...s.roiBadgeFree }}>FREE</span>
                  ) : spend > 0 ? (
                    <span style={{ ...s.roiBadge, ...(roas >= 1 ? s.roiBadgeGreen : s.roiBadgeRed) }}>{roas.toFixed(1)}x ROAS</span>
                  ) : null}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
                  <div><div style={{ fontSize: 10, color: 'var(--muted)' }}>Visitors</div><div style={{ fontWeight: 600, fontSize: 13, fontFamily: 'var(--font-mono)' }}>{p.visitors.toLocaleString()}</div></div>
                  <div><div style={{ fontSize: 10, color: 'var(--muted)' }}>Conversions</div><div style={{ fontWeight: 600, fontSize: 13, fontFamily: 'var(--font-mono)' }}>{p.bookings}</div></div>
                  <div><div style={{ fontSize: 10, color: 'var(--muted)' }}>Revenue</div><div style={{ fontWeight: 600, fontSize: 13, fontFamily: 'var(--font-mono)' }}>${revenue.toFixed(0)}</div></div>
                </div>
                {spend > 0 && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line-card)', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
                    <div><div style={{ fontSize: 10, color: 'var(--muted)' }}>Ad Spend</div><div style={{ fontWeight: 600, fontSize: 13, fontFamily: 'var(--font-mono)' }}>${spend.toFixed(0)}</div></div>
                    <div><div style={{ fontSize: 10, color: 'var(--muted)' }}>CPA</div><div style={{ fontWeight: 600, fontSize: 13, fontFamily: 'var(--font-mono)' }}>{p.bookings > 0 ? `$${(spend / p.bookings).toFixed(2)}` : 'N/A'}</div></div>
                    <div><div style={{ fontSize: 10, color: 'var(--muted)' }}>Profit</div><div style={{ fontWeight: 600, fontSize: 13, fontFamily: 'var(--font-mono)', color: profit >= 0 ? '#059669' : '#DC2626' }}>{profit >= 0 ? '+' : ''}${profit.toFixed(0)}</div></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Charts Row */}
      <div style={{ ...s.row, ...s.row2 }}>
        <div style={s.card}>
          <div style={s.cardTitle}>Traffic Source Breakdown</div>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={100} paddingAngle={3} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v) => [`${v} visitors`]} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '80px 0' }}>No visitor data yet</div>}
        </div>

        <div style={s.card}>
          <div style={s.cardTitle}>Daily Visitors by Source</div>
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                {Object.keys(SOURCE_COLORS).filter(k => k !== 'Unknown').map(type => (
                  <Line key={type} type="monotone" dataKey={type} stroke={SOURCE_COLORS[type]} strokeWidth={2} dot={false} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '80px 0' }}>No trend data yet</div>}
        </div>
      </div>

      {/* Monthly Performance */}
      <div style={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={s.cardTitle}>Monthly Performance</div>
          <button style={s.exportBtn} onClick={() => exportToCSV(monthlyData.map(m => ({
            Month: m.month, Visitors: m.visitors || 0, Conversions: m.bookings || 0,
            Revenue: `$${(m.revenue || 0).toFixed(2)}`,
            Conversion: m.visitors > 0 ? `${((m.bookings || 0) / m.visitors * 100).toFixed(1)}%` : '0.0%'
          })), 'monthly_performance')}>Export CSV</button>
        </div>
        {monthlyData.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="visitors" name="Visitors" fill="#3B82F6" radius={[0, 0, 0, 0]} />
                <Bar yAxisId="left" dataKey="bookings" name="Conversions" fill="#10B981" radius={[0, 0, 0, 0]} />
                <Bar yAxisId="right" dataKey="revenue" name="Revenue ($)" fill="#F59E0B" radius={[0, 0, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <table style={{ ...s.table, marginTop: 16 }}>
              <thead><tr>
                <th style={s.th}>Month</th>
                <th style={{ ...s.th, ...s.thRight }}>Visitors</th>
                <th style={{ ...s.th, ...s.thRight }}>Conversions</th>
                <th style={{ ...s.th, ...s.thRight }}>Revenue</th>
                <th style={s.th}>Conversion</th>
              </tr></thead>
              <tbody>
                {monthlyData.map((m, i) => {
                  const conv = m.visitors > 0 ? ((m.bookings || 0) / m.visitors * 100).toFixed(1) : '0.0';
                  return (
                    <tr key={i}>
                      <td style={{ ...s.td, fontWeight: 500 }}>{m.month}</td>
                      <td style={{ ...s.td, ...s.tdRight }}>{(m.visitors || 0).toLocaleString()}</td>
                      <td style={{ ...s.td, ...s.tdRight }}>{m.bookings || 0}</td>
                      <td style={{ ...s.td, ...s.tdRight }}>${(m.revenue || 0).toFixed(2)}</td>
                      <td style={s.td}>
                        <div style={s.convCell}>
                          <div style={s.progressBar}>
                            <div style={{ ...s.progressFill, width: `${Math.min(parseFloat(conv), 100)}%`, background: getConvColor(conv) }} />
                          </div>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, minWidth: 40, textAlign: 'right' }}>{conv}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        ) : <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '40px 0' }}>Monthly data will appear after more visits</div>}
      </div>

      {/* Platform Performance Table */}
      <div style={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={s.cardTitle}>Platform Performance & Conversion</div>
          <button style={s.exportBtn} onClick={() => exportToCSV(platformData.map(p => ({
            Platform: p.platform, Type: p.type, Visitors: p.visitors, Conversions: p.bookings,
            Revenue: `$${(p.revenue || 0).toFixed(2)}`, 'Ad Spend': `$${getSpend(p.platform).toFixed(2)}`,
            ROAS: getSpend(p.platform) > 0 ? `${((p.revenue || 0) / getSpend(p.platform)).toFixed(2)}x` : 'N/A',
            Conversion: `${p.conversionRate}%`
          })), 'platform_performance')}>Export CSV</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Platform</th>
              <th style={s.th}>Type</th>
              <th style={{ ...s.th, ...s.thRight }}>Visitors</th>
              <th style={{ ...s.th, ...s.thRight }}>Conversions</th>
              <th style={{ ...s.th, ...s.thRight }}>Revenue</th>
              <th style={{ ...s.th, ...s.thRight }}>Ad Spend</th>
              <th style={{ ...s.th, ...s.thRight }}>ROAS</th>
              <th style={s.th}>Conversion</th>
            </tr></thead>
            <tbody>
              {platformData.length === 0 ? (
                <tr><td colSpan={8} style={{ ...s.td, textAlign: 'center', padding: 32, color: 'var(--muted)' }}>No visitor data yet</td></tr>
              ) : platformData.map((p, i) => {
                const spend = getSpend(p.platform);
                const roas = spend > 0 ? ((p.revenue || 0) / spend) : null;
                return (
                  <tr key={i}>
                    <td style={{ ...s.td, fontWeight: 600 }}>{p.platform}</td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: `${SOURCE_COLORS[p.type] || '#6B7280'}15`, color: SOURCE_COLORS[p.type] || '#6B7280' }}>{p.type}</span>
                    </td>
                    <td style={{ ...s.td, ...s.tdRight }}>{p.visitors.toLocaleString()}</td>
                    <td style={{ ...s.td, ...s.tdRight }}>{p.bookings}</td>
                    <td style={{ ...s.td, ...s.tdRight }}>${(p.revenue || 0).toFixed(2)}</td>
                    <td style={{ ...s.td, ...s.tdRight, color: spend > 0 ? 'var(--ink)' : 'var(--muted-2)' }}>{spend > 0 ? `$${spend.toFixed(0)}` : '-'}</td>
                    <td style={{ ...s.td, ...s.tdRight }}>
                      {roas !== null ? (
                        <span style={{ ...s.badge, background: roas >= 1 ? '#D1FAE5' : '#FEE2E2', color: roas >= 1 ? '#059669' : '#DC2626', fontWeight: 700 }}>{roas.toFixed(1)}x</span>
                      ) : <span style={{ color: 'var(--muted-2)' }}>-</span>}
                    </td>
                    <td style={s.td}>
                      <div style={s.convCell}>
                        <div style={s.progressBar}>
                          <div style={{ ...s.progressFill, width: `${Math.min(parseFloat(p.conversionRate), 100)}%`, background: getConvColor(p.conversionRate) }} />
                        </div>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, minWidth: 40, textAlign: 'right' }}>{p.conversionRate}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Landing Pages */}
      <div style={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={s.cardTitle}>Top Landing Pages</div>
          <button style={s.exportBtn} onClick={() => exportToCSV((data?.byLandingPage || []).map(l => ({
            Page: l.landingPage, Visitors: l.visitors, Conversions: l.bookings || 0,
            Revenue: `$${(l.revenue || 0).toFixed(2)}`, Conversion: `${l.conversionRate || '0.0'}%`
          })), 'landing_pages')}>Export CSV</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Page</th>
              <th style={{ ...s.th, ...s.thRight }}>Visitors</th>
              <th style={{ ...s.th, ...s.thRight }}>Conversions</th>
              <th style={{ ...s.th, ...s.thRight }}>Revenue</th>
              <th style={s.th}>Conversion</th>
            </tr></thead>
            <tbody>
              {(data?.byLandingPage || []).length === 0 ? (
                <tr><td colSpan={5} style={{ ...s.td, textAlign: 'center', padding: 24, color: 'var(--muted)' }}>No data yet</td></tr>
              ) : (data?.byLandingPage || []).map((l, i) => {
                const conv = parseFloat(l.conversionRate || '0');
                return (
                  <tr key={i}>
                    <td style={{ ...s.td, fontFamily: 'var(--font-mono)', fontSize: 12, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.landingPage}</td>
                    <td style={{ ...s.td, ...s.tdRight }}>{l.visitors.toLocaleString()}</td>
                    <td style={{ ...s.td, ...s.tdRight }}>{l.bookings || 0}</td>
                    <td style={{ ...s.td, ...s.tdRight }}>${(l.revenue || 0).toFixed(2)}</td>
                    <td style={s.td}>
                      <div style={s.convCell}>
                        <div style={s.progressBar}>
                          <div style={{ ...s.progressFill, width: `${Math.min(conv, 100)}%`, background: getConvColor(conv) }} />
                        </div>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, minWidth: 40, textAlign: 'right' }}>{l.conversionRate || '0.0'}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Device & Browser */}
      <div style={{ ...s.row, ...s.row3 }}>
        <div style={s.card}>
          <div style={s.cardTitle}>Devices</div>
          {(data?.byDevice || []).map((d, i) => {
            const pct = totalVisitors > 0 ? (d.count / totalVisitors * 100) : 0;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ minWidth: 70, fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>{d.device === 'unknown' ? 'Other' : d.device.charAt(0).toUpperCase() + d.device.slice(1)}</span>
                <div style={{ ...s.progressBar, height: 8 }}>
                  <div style={{ ...s.progressFill, height: 8, width: `${pct}%`, background: ['#3B82F6', '#10B981', '#F59E0B', '#D1D5DB'][i] || '#888' }} />
                </div>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, minWidth: 60, textAlign: 'right' }}>{d.count} ({pct.toFixed(0)}%)</span>
              </div>
            );
          })}
          {(!data?.byDevice || data.byDevice.length === 0) && <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '40px 0' }}>No data yet</div>}
        </div>

        <div style={s.card}>
          <div style={s.cardTitle}>Browsers</div>
          {(data?.byBrowser || []).map((b, i) => {
            const pct = totalVisitors > 0 ? (b.count / totalVisitors * 100) : 0;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ minWidth: 70, fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>{b.browser || 'Other'}</span>
                <div style={{ ...s.progressBar, height: 8 }}>
                  <div style={{ ...s.progressFill, height: 8, width: `${pct}%`, background: PLATFORM_COLORS[i % PLATFORM_COLORS.length] }} />
                </div>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, minWidth: 40, textAlign: 'right' }}>{b.count}</span>
              </div>
            );
          })}
          {(!data?.byBrowser || data.byBrowser.length === 0) && <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '40px 0' }}>No data yet</div>}
        </div>
      </div>

      {/* CSS animation for live dot */}
      <style>{`@keyframes pulse { 0% { opacity:1 } 50% { opacity:0.4 } 100% { opacity:1 } }`}</style>
    </div>
  );
}
